import { ActionIcon, ActionIconProps, Group, Tooltip } from '@mantine/core'
import { useTimeout } from '@mantine/hooks'
import { IconCopy } from '@tabler/icons-react'
import { PropsWithChildren, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type CopyableTextProps = PropsWithChildren<{
  text?: string
  iconSize?: ActionIconProps['size']
  timeout?: number
}>

// navigator.clipboard needs a secure origin, and the platform is also reached over plain http.
const copyBySelection = (text: string) => {
  const field = document.createElement('textarea')
  field.value = text
  field.setAttribute('readonly', '')
  field.style.position = 'fixed'
  field.style.opacity = '0'
  document.body.appendChild(field)
  field.select()
  const copied = document.execCommand('copy')
  document.body.removeChild(field)
  return copied
}

export const CopyableText = ({
  text,
  iconSize = 'sm',
  timeout = 2000,
  children
}: CopyableTextProps) => {
  const intl = useIntl()
  const [copied, setCopied] = useState(false)
  const { start, clear } = useTimeout(() => setCopied(false), timeout)

  const handleCopyClick = async () => {
    if (!text) return

    let succeeded = true
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      succeeded = copyBySelection(text)
    }
    if (!succeeded) return

    setCopied(true)
    clear()
    start()
  }

  return (
    <Group wrap="nowrap" gap={4}>
      <div>{children}</div>

      {text && (
        <Tooltip
          label={
            copied ? (
              <FormattedMessage id="general.clipboard.copied" />
            ) : (
              <FormattedMessage id="general.clipboard.copy" />
            )
          }
        >
          <ActionIcon
            variant="subtle"
            size={iconSize}
            aria-label={intl.formatMessage({ id: 'general.clipboard.copy' })}
            onClick={() => void handleCopyClick()}
          >
            <IconCopy style={{ width: '80%', height: '80%' }} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  )
}
